import { useState } from 'react';
import { ALL_TILES } from '../engine/facts';
import type { Tile } from '../engine/facts';
import type { Mode } from '../engine/exercises/compare';
import type { ProfileRow } from '../data/db';
import type { Animal, Color } from '../data/looks';
import { AnimalIcon, COLOR_LABELS, LABELS } from './animals';
import { InstallHint } from './InstallHint';
import { Hint, PrimaryButton, Screen, Title } from './kit';

const MODES: { id: Mode; label: string }[] = [
  { id: 'mul', label: 'Только умножение' },
  { id: 'div', label: 'Только деление' },
  { id: 'mixed', label: 'Умножение и деление' },
];

interface Props {
  /** Чей это планшет прямо сейчас. Показывается зверем, а не текстом:
   *  ребёнок узнаёт себя по картинке, а имени у профиля нет вовсе. */
  profile: ProfileRow;
  onProfiles: () => void;
  /** Одна строка о состоянии обмена, или `null`, когда всё в порядке. */
  syncText?: string | null;
  onStart: (tiles: Tile[], mode: Mode) => void;
  onProgress: () => void;
  onHistory: () => void;
}

/** Фон задают ТОЛЬКО варианты `OFF` и `ON`, а не общая часть.
 *  У `.bg-card` и `.bg-accent-soft` одинаковая специфичность, и побеждает
 *  тот, кто ниже в собранном файле, — а это `.bg-card`. Пока фон стоял в
 *  общей части, голубая заливка выбранной плитки не появлялась НИКОГДА, и
 *  ребёнок не видел, какие таблицы отметил. Ни сборка, ни типы, ни тесты
 *  об этом не говорят. */
const CHOICE = 'rounded-xl border-2 p-3 font-semibold';
const OFF = 'border-line bg-card text-ink';
const ON = 'border-accent bg-accent-soft text-accent';

export function HomeScreen({
  profile, onProfiles, syncText, onStart, onProgress, onHistory,
}: Props) {
  /** Предыдущий выбор намеренно не восстанавливается (§4 спеки). */
  const [tiles, setTiles] = useState<Tile[]>([]);
  const [mode, setMode] = useState<Mode | null>(null);

  const toggle = (tile: Tile) =>
    setTiles((current) =>
      current.includes(tile) ? current.filter((t) => t !== tile) : [...current, tile],
    );

  return (
    <Screen>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onProfiles}
          aria-label={`Профиль: ${COLOR_LABELS[profile.color as Color]} ${LABELS[profile.animal as Animal]}. Сменить`}
          className="rounded-xl border border-line bg-card p-1"
        >
          <AnimalIcon
            animal={profile.animal as Animal}
            color={profile.color as Color}
            size={32}
          />
        </button>
        <Title>Таблица умножения</Title>
        <nav className="ml-auto flex gap-1">
          <button
            type="button"
            onClick={onProgress}
            className="min-h-11 rounded-lg border border-line bg-card px-3 py-2 text-sm text-ink"
          >
            Успехи
          </button>
          <button
            type="button"
            onClick={onHistory}
            className="min-h-11 rounded-lg border border-line bg-card px-3 py-2 text-sm text-ink"
          >
            История
          </button>
        </nav>
      </div>
      {syncText && <p className="m-0 text-[0.9rem] text-muted">{syncText}</p>}
      <InstallHint />

      <Hint>Выбери таблицы</Hint>
      <div className="grid grid-cols-5 gap-2">
        {ALL_TILES.map((tile) => (
          <button
            key={tile}
            type="button"
            aria-pressed={tiles.includes(tile)}
            className={`${CHOICE} ${tiles.includes(tile) ? ON : OFF} py-4 text-[1.05rem]`}
            onClick={() => toggle(tile)}
          >
            ×{tile}
          </button>
        ))}
      </div>

      <Hint>Что тренируем</Hint>
      <div className="grid gap-2">
        {MODES.map((item) => (
          <button
            key={item.id}
            type="button"
            aria-pressed={mode === item.id}
            className={`${CHOICE} ${mode === item.id ? ON : OFF} px-4 py-3 text-left text-base`}
            onClick={() => setMode(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <PrimaryButton
        bottom
        disabled={tiles.length === 0 || mode === null}
        onClick={() => mode && onStart(tiles, mode)}
      >
        Начать
      </PrimaryButton>
    </Screen>
  );
}
